import { useTranslations } from '@vocab/react';
import { Notice, Stack, Text, TextLinkButton } from 'braid-design-system';
import { useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { VirusScanStatus } from 'src/__generated__/graphql';
import {
  CoverLetterFields,
  type CoverLetterFieldsValues,
} from 'src/components/CoverLetterFields/CoverLetterFields';
import { FileAttachment } from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import { useVirusScan } from 'src/hooks/useVirusScan';
import DrawerDocument from 'src/pages/Review/components/DrawerDocument';

import translations from './.vocab';

interface PropsCoverLetterItem {
  onChange: (values: Partial<CoverLetterFieldsValues>) => void;
}

export function CoverLetterItem({ onChange }: PropsCoverLetterItem) {
  const { t } = useTranslations(translations);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const rootForm = useFormContext<CoverLetterFieldsValues>();

  const form = useForm();
  const { handleSubmit, reset, formState } = form;

  useVirusScan({
    fileUri: rootForm.watch('coverLetter-uri'),
    currentMethod: rootForm.watch('coverLetter-method'),
    onStatusChange: (virusScanStatus, method) => {
      const shouldCheck = method === FileAttachment.UPLOAD;
      if (!shouldCheck) {
        rootForm.clearErrors('coverLetter-virusScan');
      } else if (virusScanStatus === VirusScanStatus.HasVirus) {
        rootForm.setError('coverLetter-virusScan', {
          type: VirusScanStatus.HasVirus,
          message: t('error.virus.document', {
            Button: (text) => text,
          })?.toString(),
        });
      } else if (virusScanStatus === VirusScanStatus.Pending) {
        rootForm.setError('coverLetter-virusScan', {
          type: VirusScanStatus.Pending,
          message: t('error.virus.pending'),
        });
      } else {
        rootForm.clearErrors('coverLetter-virusScan');
      }
    },
  });

  const coverLetterText = rootForm.watch('coverLetter-text');
  const coverLetterFile = rootForm.watch('coverLetter-file');
  const coverLetterMethod = rootForm.watch('coverLetter-method');

  let currentSelectionText = t('No cover letter');
  let currentSelectionTestId = 'cover-letter-dont';
  if (coverLetterMethod === FileAttachment.CHANGE) {
    currentSelectionText = t('You wrote a cover letter');
    currentSelectionTestId = 'cover-letter-write';
  }

  if (coverLetterMethod === FileAttachment.UPLOAD) {
    currentSelectionText = coverLetterFile.name;
    currentSelectionTestId = 'cover-letter-upload';
  }

  const closeForm = () => {
    setIsFormOpen(false);
    reset(rootForm.getValues());
  };

  const confirmEdit = (values: Partial<CoverLetterFieldsValues>) => {
    onChange(values);
    setIsFormOpen(false);
  };

  const virusScanStatus =
    rootForm.formState.errors['coverLetter-virusScan']?.type;

  return (
    <Stack space="small">
      <DrawerDocument
        title={t('Cover letter')}
        currentSelectionText={currentSelectionText}
        currentSelectionTestId={currentSelectionTestId}
        editButtonLabel={t('Edit cover letter')}
        editButtonId="edit-cover-letter"
        onSubmit={handleSubmit(confirmEdit)}
        onClose={closeForm}
        shouldConfirmBeforeClose={formState.isDirty}
        isFormOpen={isFormOpen}
        setIsFormOpen={setIsFormOpen}
      >
        <FormProvider {...form}>
          <CoverLetterFields
            hideTitle
            defaultMethod={coverLetterMethod}
            defaultValue={
              // eslint-disable-next-line no-nested-ternary
              coverLetterMethod === FileAttachment.CHANGE
                ? coverLetterText
                : coverLetterMethod === FileAttachment.UPLOAD
                  ? coverLetterFile
                  : undefined
            }
          />
        </FormProvider>
      </DrawerDocument>
      {virusScanStatus === VirusScanStatus.HasVirus && (
        <Notice tone="critical">
          <Text size="small">
            {t('error.virus.document', {
              Button: (text) => (
                <TextLinkButton onClick={() => setIsFormOpen(true)}>
                  {text}
                </TextLinkButton>
              ),
            })}
          </Text>
        </Notice>
      )}
      {virusScanStatus === VirusScanStatus.Pending && (
        <Notice tone="critical">
          <Text size="small">{t('error.virus.pending')}</Text>
        </Notice>
      )}
    </Stack>
  );
}
